import React, { useEffect, useRef, useState } from 'react';
import { captureSignupIntent } from '../modules/onboarding/signupIntent';
import { captureSignupLanding } from '../modules/onboarding/signupLanding';
import { captureCheckoutIntent } from '../modules/music24/config/checkoutIntent';
import { TypedMemo } from '../modules/common/types';
import { useAccountId, useUserTokenDecoded } from '../redux/slices/appInfo/hooks';
import { RouterContext } from '../contexts/contexts';
import { useSMLRouter } from './sml/hooks';
import { SMLAnalytics } from '../modules/analytics/sml/analytics';
import { anonymousIdentifier } from '../modules/analytics/sml/anonymousIdentifier';
import { ShareTokenProvider } from '../modules/music24/contexts/ShareTokenContext';
import { useReferralCapture } from '../modules/music24/hooks/useReferralCapture';
import { ScreenViewTracker } from '../modules/analytics/sml/ScreenViewTracker';
import { PlausibleTracker } from '../modules/analytics/sml/PlausibleTracker';
import { SMLThemeProvider } from '../modules/music24/theme/SMLThemeProvider';
import { TrackingConsentBar } from '../modules/analytics/consent/TrackingConsentBar';
import { useTrackingConsent } from '../modules/analytics/consent/useTrackingConsent';

const ReferralCapture: React.FC = () => {
  useReferralCapture();
  return null;
};

const SMLRootWrapperComponent: React.FC<React.PropsWithChildren> = ({ children }) => {
  useState(() => {
    captureSignupIntent();
    captureSignupLanding();
    captureCheckoutIntent();
    return true;
  });
  const accountId = useAccountId();
  const userTokenDecoded = useUserTokenDecoded();
  // The identities go to the trackers only after Accept. They run again when the consent arrives
  // (TrackingConsentBar, a child, starts the trackers first: child effects run before these).
  const trackingGranted = useTrackingConsent() === 'granted';

  useEffect(() => {
    if (!trackingGranted) {
      return;
    }
    anonymousIdentifier()
      .then((aId) => {
        console.log('Anonymous identity is', aId);
        SMLAnalytics.identifyAnonymous(aId).catch(console.error);
      })
      .catch(console.error);
  }, [trackingGranted]);

  const wasSignedIn = useRef(false);
  useEffect(() => {
    if (userTokenDecoded !== null) {
      wasSignedIn.current = true;
      return;
    }
    // Sign-out: `signed_out` goes with the user, then the next events carry none (the install
    // id stays). A page load that starts signed out sends nothing.
    if (wasSignedIn.current) {
      wasSignedIn.current = false;
      SMLAnalytics.track('signed_out', {}).catch(console.error);
    }
    SMLAnalytics.clearIdentity();
  }, [userTokenDecoded]);

  useEffect(() => {
    if (userTokenDecoded === null || accountId === null) {
      return;
    }
    const { id, licenseId } = userTokenDecoded;
    if (licenseId === undefined) {
      return;
    }
    console.log('Updating identity to', { userId: id, accountId, licenseId });
    SMLAnalytics.sentryIdentity(id, accountId, licenseId);
    if (!trackingGranted) {
      return;
    }
    (async () => {
      const aId = await anonymousIdentifier();
      await SMLAnalytics.identify(id, licenseId, aId);
    })().catch(console.error);
  }, [accountId, userTokenDecoded, trackingGranted]);

  return (
    <SMLThemeProvider>
      <ShareTokenProvider>
        <ReferralCapture />
        <PlausibleTracker />
        <ScreenViewTracker />
        <TrackingConsentBar />
        <RouterContext.Provider value={useSMLRouter}>{children}</RouterContext.Provider>
      </ShareTokenProvider>
    </SMLThemeProvider>
  );
};

export const SMLRootWrapper = TypedMemo(SMLRootWrapperComponent);
